/**
 * Task status presentation shared by the detail panels and the deleted-tasks
 * dialog. Lives apart from any one component so a consumer needing two lookups
 * does not pull a whole panel's import graph along with them.
 */

import type { TaskStatus } from '../../services/api/types.gen';

export const STATUS_DISPLAY_LABELS: Record<string, string> = {
  not_started: 'Not Started',
  saved: 'Saved',
  returned_for_review: 'Returned for Review',
  submitted_with_signature: 'Submitted (Signed)',
  submitted: 'Submitted',
  pending_clinician_signature: 'Pending Clinician Signature',
  completed: 'Completed',
  exported: 'Exported',
  reopened: 'Reopened',
  sent_to_physician: 'Sent to Physician',
  returned_with_signature: 'Returned with Signature',
  sent_no_signature_required: 'Completed',
};

export function getStatusBadgeVariant(
  status: TaskStatus
): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'completed': // fallthrough
    case 'exported':
      return 'default';
    case 'submitted_with_signature':
    case 'pending_clinician_signature':
      return 'secondary';
    case 'returned_for_review':
      return 'destructive';
    case 'not_started':
    case 'saved':
    case 'submitted':
    case 'reopened':
      return 'outline';
    default:
      return 'outline';
  }
}

/**
 * Human label for a status, falling back to Title Case of the raw value.
 *
 * Takes `string` so a status the generated types do not know about yet still
 * renders readably instead of falling through the map as a raw enum value.
 */
export const getStatusLabel = (status: string): string =>
  STATUS_DISPLAY_LABELS[status] ??
  status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
